import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { ProductFields } from "@tenforce/product-fields"

import env from "../../../../env"
import { GlobalState } from "../../../../store"
import { addAttachmentDataApi } from "../../../../apis/attachments"
import { addErrorNotification } from "../../../notifications/actions"
import { IRichTextField } from "../../"
import { AttachmentDTO } from "../../../../apis/models/attachments"

export interface IRichTextState {
    isLoading: boolean
    attachments: ProductFields.Attachment[]
}

interface RichTextAttachmentParameters {
    field: IRichTextField
    files: File[]
}

export const initialState: IRichTextState = {
    isLoading: false,
    attachments: [],
}

export const defaultAddingAttachmentErrorMessage =
    "Something went wrong adding the attachment "

export const richTextFieldSlice = createSlice({
    name: "richtextField",

    reducers: {},

    initialState: initialState,

    extraReducers: (builder) => {
        builder
            .addCase(addAttachment.pending, (state) => {
                state.isLoading = true
            })
            .addCase(addAttachment.fulfilled, (state, action) => {
                state.isLoading = false
                state.attachments = action.payload || []
            })
            .addCase(addAttachment.rejected, (state) => {
                state.isLoading = false
            })
    },
})

export default richTextFieldSlice.reducer

export const addAttachment = createAsyncThunk(
    "fields/richText/addAttachment",
    async ({ files, field }: RichTextAttachmentParameters, { dispatch }) => {
        const promises: Promise<AttachmentDTO>[] = []
        const attachmentResults: ProductFields.Attachment[] = []
        files.map((file) => {
            promises.push(addAttachmentDataApi(field.itemId, file))
        })
        try {
            const responses = await Promise.allSettled(promises)

            responses.map(async (response, index) => {
                if (response.status === "fulfilled") {
                    const { value } = response
                    attachmentResults.push({
                        id: value.Id,
                        fileProperties: value.Description,
                        fileName: value.FileName,
                        url: `${env.applicationPath}/download/attachment/${value.Id}`,
                        type: files[index].type,
                    })
                } else {
                    const error = response.reason
                    const fileName = files[index].name
                    let errorMessage = `${defaultAddingAttachmentErrorMessage} file: ${fileName} in ${field.title}`
                    console.error(`${errorMessage} (${field.fieldId}): `, error)
                    if (error instanceof Error) {
                        errorMessage = `${errorMessage}. ${error.message}`
                    }
                    dispatch(addErrorNotification(errorMessage))
                }
            })
            return attachmentResults
        } catch (error) {
            let errorMessage = `${defaultAddingAttachmentErrorMessage}${field.title}.`
            if (error instanceof Error) {
                errorMessage = `${errorMessage}. ${error.message}`
            }
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            dispatch(addErrorNotification(errorMessage))
        }
        return []
    },
    {
        condition: (_, { getState }) => {
            const { fields } = getState() as GlobalState
            if (fields.id === -1 || fields.isFetchingFields) {
                // no react fields are present or
                // fetching is in progress
                return false
            }
        },
    },
)
